import React, { useState } from 'react';
import axios from 'axios';
import { X, ShieldCheck, Smartphone, KeyRound, AlertTriangle } from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../../theme';

const overlay = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modal = {
    backgroundColor: 'white', borderRadius: '16px',
    padding: '28px 32px', width: '500px', maxWidth: '95vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
const btn = (color) => ({
    flex: 1, padding: '11px', borderRadius: '10px', border: 'none',
    backgroundColor: color, color: 'white', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
});

/**
 * Admin opens this from the dashboard widget when a device requests access.
 *  Step 1 — show device details, big "Send my OTP" button (sends OTP to admin's phone)
 *  Step 2 — admin enters mobile OTP + Authenticator TOTP, click Approve
 *  On success — backend issues an unlock code to the requester's mobile
 */
export default function DeviceApprovalModal({ API_BASE, device, onClose, onDone }) {
    const [step, setStep] = useState(1);
    const [adminOtp, setAdminOtp] = useState('');
    const [totp,    setTotp]     = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [useRecovery,  setUseRecovery]  = useState(false);
    const [busy, setBusy] = useState(false);
    const [err,  setErr]  = useState('');
    const [otpInfo, setOtpInfo] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [showReject, setShowReject] = useState(false);

    const sendAdminOtp = async () => {
        setBusy(true); setErr('');
        try {
            const r = await axios.post(`${API_BASE}/security/admin-send-otp`);
            setOtpInfo(r.data);
            setStep(2);
        } catch (e) { setErr(e.response?.data?.error || 'Failed to send OTP'); }
        setBusy(false);
    };

    const approve = async () => {
        if (adminOtp.length !== 6) return setErr('Mobile OTP must be 6 digits');
        if (useRecovery) {
            if (recoveryCode.trim().length < 8) return setErr('Recovery code looks too short (format: XXXX-XXXX)');
        } else {
            if (totp.length !== 6) return setErr('Authenticator code must be 6 digits');
        }
        setBusy(true); setErr('');
        try {
            const body = useRecovery
                ? { deviceId: device._id, adminOtp, recoveryCode: recoveryCode.trim().toUpperCase() }
                : { deviceId: device._id, adminOtp, totp };
            await axios.post(`${API_BASE}/security/device-approve`, body);
            onDone && onDone('approved');
        } catch (e) { setErr(e.response?.data?.error || 'Approval failed'); }
        setBusy(false);
    };

    const reject = async () => {
        setBusy(true); setErr('');
        try {
            await axios.post(`${API_BASE}/security/device-reject`, {
                deviceId: device._id, reason: rejectReason,
            });
            onDone && onDone('rejected');
        } catch (e) { setErr(e.response?.data?.error || 'Reject failed'); }
        setBusy(false);
    };

    return (
        <div style={overlay} onClick={onClose}>
            <div style={modal} onClick={e=>e.stopPropagation()}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                        <ShieldCheck size={22} color={theme.primary}/>
                        <h2 style={{margin:0, color:theme.navy, fontSize:'1.1rem'}}>Approve Device Access</h2>
                    </div>
                    <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer'}}><X size={20} color={theme.textMuted}/></button>
                </div>

                {/* Device summary */}
                <div style={{padding:'14px', backgroundColor:'#f8fafc', borderRadius:'10px', marginBottom:'16px', fontSize:'0.85rem'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px'}}>
                        <Smartphone size={16} color={theme.primary}/>
                        <strong style={{color:theme.navy}}>{device.name || 'Unnamed PC'}</strong>
                    </div>
                    <div style={{color:theme.textMuted, fontSize:'0.8rem'}}>
                        <div>Requester: <strong>{device.requesterName || 'Unknown'}</strong></div>
                        <div>Mobile: {device.requesterMobile} ({device.requesterChannel})</div>
                        <div>IP: {device.ip || '—'}</div>
                        {device.requestReason && <div style={{marginTop:'4px', padding:'6px 8px', backgroundColor:'white', borderRadius:'6px'}}>"{device.requestReason}"</div>}
                    </div>
                </div>

                {err && (
                    <div style={{padding:'10px 14px', borderRadius:'8px', backgroundColor:'#fef2f2', color:theme.danger, fontSize:'0.85rem', marginBottom:'14px', display:'flex', gap:'8px', alignItems:'center'}}>
                        <AlertTriangle size={16}/>{err}
                    </div>
                )}

                {showReject ? (<>
                    <label style={labelStyle}>Reason for rejection</label>
                    <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} style={{...inputStyle, resize:'vertical', minHeight:'60px'}}/>
                    <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                        <button onClick={()=>setShowReject(false)} style={{...btn(theme.textMuted), flex:'0 0 auto', paddingLeft:'18px', paddingRight:'18px'}}>Cancel</button>
                        <button onClick={reject} disabled={busy} style={btn(theme.danger)}>{busy ? 'Rejecting…' : 'Confirm Reject'}</button>
                    </div>
                </>) : step === 1 ? (<>
                    <p style={{fontSize:'0.85rem', color:theme.textMuted, margin:'0 0 14px'}}>
                        To approve, you'll need <strong>two factors</strong>: an OTP sent to your registered mobile + your Google Authenticator code.
                    </p>
                    <div style={{display:'flex', gap:'8px'}}>
                        <button onClick={()=>setShowReject(true)} style={{...btn(theme.textMuted)}}>Reject</button>
                        <button onClick={sendAdminOtp} disabled={busy} style={btn(theme.primary)}>{busy ? 'Sending…' : 'Send OTP to my mobile'}</button>
                    </div>
                </>) : (<>
                    <div style={{padding:'10px 12px', backgroundColor:'#f0fdf4', borderRadius:'8px', fontSize:'0.82rem', color:'#15803d', marginBottom:'14px'}}>
                        ✓ OTP sent to your registered mobile{otpInfo?.channel ? ` via ${otpInfo.channel}` : ''}.
                    </div>
                    <label style={labelStyle}>OTP from your mobile</label>
                    <input value={adminOtp} onChange={e=>setAdminOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                        style={{...inputStyle, fontSize:'1.2rem', textAlign:'center', letterSpacing:'0.4em', fontFamily:'monospace'}}
                        placeholder="000000" maxLength={6}/>
                    {!useRecovery ? (<>
                        <label style={labelStyle}>Code from Google Authenticator</label>
                        <input value={totp} onChange={e=>setTotp(e.target.value.replace(/\D/g,'').slice(0,6))}
                            style={{...inputStyle, fontSize:'1.2rem', textAlign:'center', letterSpacing:'0.4em', fontFamily:'monospace'}}
                            placeholder="000000" maxLength={6}/>
                        <button onClick={()=>{setUseRecovery(true); setErr('');}} type="button"
                            style={{background:'none', border:'none', color:theme.primary, cursor:'pointer', fontSize:'0.78rem', fontWeight:'600', padding:'8px 0 0', display:'flex', alignItems:'center', gap:'4px'}}>
                            <KeyRound size={12}/> Lost authenticator? Use a recovery code
                        </button>
                    </>) : (<>
                        <label style={labelStyle}>Recovery Code (one-time use)</label>
                        <input value={recoveryCode} onChange={e=>setRecoveryCode(e.target.value.toUpperCase())}
                            style={{...inputStyle, fontSize:'1rem', textAlign:'center', letterSpacing:'0.2em', fontFamily:'monospace'}}
                            placeholder="XXXX-XXXX" maxLength={9}/>
                        <button onClick={()=>{setUseRecovery(false); setErr('');}} type="button"
                            style={{background:'none', border:'none', color:theme.primary, cursor:'pointer', fontSize:'0.78rem', fontWeight:'600', padding:'8px 0 0'}}>
                            ← Back to Authenticator code
                        </button>
                    </>)}
                    <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                        <button onClick={()=>setShowReject(true)} style={{...btn(theme.textMuted)}}>Reject</button>
                        <button onClick={approve} disabled={busy} style={btn(theme.success)}>{busy ? 'Approving…' : 'Approve & Send Unlock Code'}</button>
                    </div>
                </>)}
            </div>
        </div>
    );
}
